/* eslint-disable react/jsx-no-undef */
import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { LayoutChangeEvent, ViewStyle } from 'react-native'

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { DataTablePagination } from './DataTablePagination'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { Display } from '../../atoms/display/Display'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { SortIcon, SortIconDirection } from '../../atoms/icons/SortIcon'
import { LoadingSkeleton } from '../../atoms/loadingSkeleton/LoadingSkeleton'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback'
import { colors as colorsV1 } from '../../styles/colors'
import { DisplaySize, TextAlign, TitleSize } from '../../styles/typeStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { getFlexAlignFromTA, tID } from '../../utils/utils'

type PaginationOptions = {
  pageSize: number | 'dynamic'
}
const DEFAULT_PAGINATION_OPTIONS: PaginationOptions = {
  pageSize: 10,
}

export type RowActionsRenderFn<TData> = (row: Row<TData>, isHovered: boolean) => React.ReactNode

export type DataTableProps<TData> = {
  accessibilityLabel?: string
  columns: ColumnDef<TData, any>[]
  data: TData[] | undefined
  getRowId?: TableOptions<TData>['getRowId']
  emptyTableText?: string
  emptyTableComponent?: React.ReactNode
  pagination?: boolean | Partial<PaginationOptions>
  striped?: boolean
  rowBorder?: boolean
  rowHeight?: number | 'auto'
  tableContainerStyle?: ViewStyle
  /**
   * Makes every row pressable, with a pointer cursor and a hover highlight. With
   * `withTableSemantics` the row stays a `row` rather than becoming a button, so pair it with row
   * actions that offer the same behaviour to keyboard users.
   */
  onRowClick?: (row: Row<TData>) => void
  stickyColumn?: string
  cellAlignment?: TextAlign
  withRowCount?: boolean
  rightHeaderSection?: React.ReactNode
  withScroll?: boolean
  pagnationStyle?: ViewStyle
  sortingState?: SortingState
  onSortingChange?: (sorting: SortingState) => void
  hoveredRowId?: string | null
  onRowHover?: (rowId: string | null) => void
  rowActions?: RowActionsRenderFn<TData>
  rowHeaderColumn?: string
  withTableSemantics?: boolean
}

const OuterContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1,
  gap: theme.spacing['16px'],
}))

const HeaderContainer = styled.View<{ hasLeftSection: boolean }>(({ hasLeftSection }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: hasLeftSection ? 'space-between' : 'flex-end',
}))

const Table = styled.View<{ hasStickyColumn: boolean }>(({ hasStickyColumn }) => ({
  ...(hasStickyColumn && {
    display: 'table',
  }),
  flexGrow: 1,
  flexShrink: 1,
}))

const TableBodyScrollContainer = styled.View<{ withScroll: boolean }>(({ withScroll }) => ({
  overflow: withScroll ? 'auto' : undefined,
  flexGrow: 1,
  flexShrink: 1,
}))

const TableBody = styled.View({})

const TableContainer = styled.View(({ theme }) => ({
  overflow: 'auto',
  borderTopLeftRadius: '16px',
  borderTopRightRadius: '16px',
  gap: theme.spacing['16px'],
  flexGrow: 1,
  flexShrink: 1,
}))

const Header = styled.View<{
  hasStickyColumn: boolean
}>(({ hasStickyColumn, theme }) => ({
  alignSelf: 'stretch',
  flexDirection: 'row',
  padding: theme.spacing['16px'],
  borderBottomWidth: '3px',
  borderBottomColor: theme.colors.borderDefault,
  backgroundColor: theme.colors.backgroundSection,
  position: 'sticky',
  top: 0,
  ...(hasStickyColumn && {
    paddingLeft: 0,
    paddingRight: 0,
    display: 'inline-flex',
  }),
}))

const LoadingContainer = styled.View({
  justifyContent: 'center',
})

// Cell Styles
type CellArgs = {
  width?: number
  minWidth?: number
  cellFlex?: number
  shrinkToFit?: boolean
  contentPaddingLeft?: number
  align: TextAlign
  isSticky: boolean
  isRowBody: boolean
  rowIndex?: number
  striped?: boolean
}

const getCellSizingStyles = ({
  width,
  minWidth,
  cellFlex,
  shrinkToFit,
}: Pick<CellArgs, 'width' | 'minWidth' | 'cellFlex' | 'shrinkToFit'>): CSSObject => {
  if (!shrinkToFit || !width) {
    return { flex: cellFlex ?? 1 }
  }

  return {
    flexGrow: cellFlex ?? 0,
    flexShrink: 1,
    flexBasis: `${width}px`,
    minWidth: minWidth == null ? 0 : `${minWidth}px`,
    overflow: 'hidden',
    ...(cellFlex === 0 && { maxWidth: `${width}px` }),
  }
}

const getSharedCellStyles: (
  props: {
    theme: ThemeType
  } & CellArgs,
) => CSSObject = ({
  width,
  minWidth,
  cellFlex,
  shrinkToFit,
  contentPaddingLeft,
  align,
  theme,
  isSticky,
  isRowBody,
  rowIndex,
  striped,
}) => ({
  ...getCellSizingStyles({ width, minWidth, cellFlex, shrinkToFit }),
  alignItems: 'center',
  justifyContent: getFlexAlignFromTA(align),
  flexDirection: 'row',
  gap: theme.spacing['8px'],
  ...(contentPaddingLeft != null && { paddingLeft: contentPaddingLeft }),
  ...(width && { width: `${width}px` }),
  ...(isSticky && {
    position: IS_WEB ? 'sticky' : undefined,
    left: '0px',
    zIndex: 1,
    ...(isRowBody && {
      borderRightColor: theme.colors.borderDefault,
      borderRightWidth: '3px',
      backgroundColor:
        (rowIndex != null && rowIndex % 2 === 0) || !striped
          ? theme.colors.backgroundPrimary
          : theme.colors.backgroundSecondary,
    }),
    ...(!isRowBody && {
      backgroundColor: theme.colors.backgroundSection,
    }),
  }),
})

const Cell = styled.View<CellArgs>(getSharedCellStyles)
const SortableCell = styled(PressableOpacity)<CellArgs>(getSharedCellStyles)

// Row Styles
type RowArgs = {
  rowHeight: number | 'auto'
  rowIndex: number
  striped: boolean
  rowBorder: boolean
  hasStickyColumn: boolean
  isClickable?: boolean
  isHovered?: boolean
}
const getSharedRowStyles: (
  props: {
    theme: ThemeType
  } & RowArgs,
) => CSSObject = ({ rowHeight, rowIndex, striped, rowBorder, hasStickyColumn, isClickable, isHovered, theme }) => ({
  alignSelf: 'stretch',
  flexDirection: 'row',
  ...(rowHeight === 'auto' ? { minHeight: '36px' } : { height: `${rowHeight}px` }),
  paddingLeft: hasStickyColumn ? 0 : theme.spacing['16px'],
  paddingRight: hasStickyColumn ? 0 : theme.spacing['16px'],
  backgroundColor:
    isClickable && isHovered
      ? colorsV1.charcoal1
      : rowIndex % 2 === 1 && striped
        ? theme.colors.backgroundSecondary
        : theme.colors.backgroundPrimary,
  ...(isClickable && { cursor: 'pointer' }),
  ...(rowBorder && {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderDefault,
  }),
})
const RowView = styled(Flex1View)<RowArgs>(getSharedRowStyles)
const ClickableRow = styled(PressableOpacity)<RowArgs>(getSharedRowStyles)

const RowWrapper = styled.View({
  position: 'relative',
  // Keep action focus rings above neighboring row backgrounds.
  ...(IS_WEB && { zIndex: 'auto' }),
})

const RowActionsOverlay = styled.View(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing['16px'],
  top: 0,
  bottom: 0,
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2,
}))

const EmptyTable = styled(BodyText)(({ theme }) => ({
  margin: theme.spacing['48px'],
}))

export const DataTable = <TData,>({
  accessibilityLabel,
  columns,
  data,
  getRowId,
  emptyTableText = 'No data available',
  emptyTableComponent,
  pagination = false,
  striped = true,
  rowBorder = false,
  rowHeight = 36,
  tableContainerStyle,
  onRowClick,
  stickyColumn,
  cellAlignment = 'center',
  sortingState,
  onSortingChange,
  withRowCount = false,
  rightHeaderSection,
  withScroll = false,
  hoveredRowId,
  onRowHover,
  pagnationStyle,
  rowActions,
  rowHeaderColumn,
  withTableSemantics = false,
}: DataTableProps<TData>) => {
  const { colors } = useTheme()
  const [dynamicPageSize, setDynamicPageSize] = useState(8)

  const paginationOptions = useMemo(() => {
    if (typeof pagination === 'boolean') {
      return DEFAULT_PAGINATION_OPTIONS
    }
    return {
      ...DEFAULT_PAGINATION_OPTIONS,
      ...pagination,
    }
  }, [pagination])

  const tableArgs = useMemo<TableOptions<TData>>(() => {
    const standardArgs: TableOptions<TData> = {
      data: data ?? [],
      columns,
      getRowId,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      defaultColumn: {
        enableSorting: false,
      },
    }

    if (!pagination) {
      return standardArgs
    }

    return {
      ...standardArgs,
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        pagination: {
          pageSize: paginationOptions.pageSize === 'dynamic' ? dynamicPageSize : paginationOptions.pageSize,
        },
      },
      onSortingChange: onSortingChange ?? undefined,
      state: sortingState ? { sorting: sortingState } : undefined,
      enableSortingRemoval: false,
    }
  }, [columns, data, dynamicPageSize, getRowId, onSortingChange, pagination, paginationOptions.pageSize, sortingState])

  const table = useReactTable(tableArgs)

  useEffect(() => {
    if (paginationOptions.pageSize === 'dynamic') {
      table.setPageSize(dynamicPageSize)
    }
  }, [dynamicPageSize, paginationOptions.pageSize, table])

  const onLayout = useDebouncedCallback((e: LayoutChangeEvent) => {
    const effectiveRowHeight = rowHeight === 'auto' ? 36 : rowHeight
    setDynamicPageSize(Math.floor(e.nativeEvent.layout.height / effectiveRowHeight))
  }, 500)

  const renderCells = (row: Row<TData>, index: number, isRowHovered: boolean) => {
    return row.getVisibleCells().map((cell) => {
      const cellContext = { ...cell.getContext(), isRowHovered }
      return (
        <Cell
          key={cell.id}
          testID={tID(`DataTable-cell-${cell.id}`)}
          role={withTableSemantics ? (rowHeaderColumn === cell.column.id ? 'rowheader' : 'cell') : undefined}
          align={cell.column.columnDef.meta?.align ?? cellAlignment}
          cellFlex={cell.column.columnDef.meta?.flex}
          shrinkToFit={cell.column.columnDef.meta?.shrinkToFit}
          contentPaddingLeft={cell.column.columnDef.meta?.contentPaddingLeft}
          minWidth={cell.column.columnDef.minSize}
          isRowBody
          rowIndex={index}
          isSticky={stickyColumn != null && stickyColumn === cell.column.id}
          width={cell.column.columnDef.size}
          striped={striped}
        >
          {!cell.column.columnDef.meta?.inlineComponent ? (
            <BodyText color={colors.textPrimary} textAlign={cell.column.columnDef.meta?.align ?? cellAlignment}>
              {flexRender(cell.column.columnDef.cell, cellContext)}
            </BodyText>
          ) : (
            flexRender(cell.column.columnDef.cell, cellContext)
          )}
        </Cell>
      )
    })
  }
  const hasStickyColumn = !!stickyColumn
  const rows = table.getRowModel().rows
  const pageIndex = table.getState().pagination.pageIndex
  const pageSize = table.getState().pagination.pageSize
  return (
    <OuterContainer>
      {(withRowCount || rightHeaderSection) && (
        <HeaderContainer hasLeftSection={withRowCount}>
          {withRowCount &&
            (data ? (
              <Title
                size={TitleSize.XSMALL}
                color={colors.textInactive}
                text={
                  <FormattedMessage
                    defaultMessage='Showing {start}-{end} of {count, plural, one {1 result} other {{count} results}}'
                    description='Show the number of entries listed.'
                    values={{
                      start: data.length ? (pagination ? pageSize * pageIndex + 1 : 1) : 0,
                      end: pagination ? Math.min(pageSize * (pageIndex + 1), data.length) : data.length,
                      count: data.length,
                    }}
                  />
                }
              />
            ) : (
              <LoadingSkeleton height={22} width={180} />
            ))}
          {rightHeaderSection}
        </HeaderContainer>
      )}
      <TableContainer
        accessibilityLabel={withTableSemantics ? accessibilityLabel : undefined}
        role={withTableSemantics ? 'region' : undefined}
        tabIndex={0}
        accessibilityState={withTableSemantics ? { busy: data === undefined } : undefined}
        style={tableContainerStyle}
      >
        <Table
          hasStickyColumn={hasStickyColumn}
          testID={tID('DataTable-table')}
          role={withTableSemantics ? 'table' : undefined}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <Header
              key={headerGroup.id}
              testID={tID('DataTable-header')}
              hasStickyColumn={hasStickyColumn}
              role={withTableSemantics ? 'row' : undefined}
            >
              {headerGroup.headers.map((header) => {
                const isSorted = header.column.getIsSorted()
                const sortDirection =
                  typeof isSorted === 'boolean'
                    ? SortIconDirection.NONE
                    : isSorted === 'desc'
                      ? SortIconDirection.DOWN
                      : SortIconDirection.UP

                const renderedHeader = header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())
                const content = header.column.columnDef.meta?.inlineHeaderComponent ? (
                  renderedHeader
                ) : (
                  <Display
                    noRole={withTableSemantics}
                    size={DisplaySize.EYEBROW}
                    color={colors.textPrimary}
                    testID={tID(`DataTable-header-${header.id}`)}
                    textAlign={header.column.columnDef.meta?.align ?? cellAlignment}
                  >
                    {renderedHeader}
                  </Display>
                )

                const sharedProps = {
                  align: header.column.columnDef.meta?.align ?? cellAlignment,
                  cellFlex: header.column.columnDef.meta?.flex,
                  shrinkToFit: header.column.columnDef.meta?.shrinkToFit,
                  contentPaddingLeft: header.column.columnDef.meta?.contentPaddingLeft,
                  minWidth: header.column.columnDef.minSize,
                  isSticky: stickyColumn != null && stickyColumn === header.column.id,
                  isRowBody: false,
                  width: header.column.columnDef.size,
                  id: header.id,
                  testID: tID(`DataTable-cell-${header.id}`),
                }
                if (!header.column.getCanSort()) {
                  return (
                    <Cell {...sharedProps} key={header.id} role={withTableSemantics ? 'columnheader' : undefined}>
                      {content}
                    </Cell>
                  )
                }

                return (
                  <SortableCell
                    {...sharedProps}
                    key={header.column.id}
                    onPress={header.column.getToggleSortingHandler()}
                    role={withTableSemantics ? 'columnheader' : undefined}
                  >
                    {content}
                    <SortIcon direction={sortDirection} />
                  </SortableCell>
                )
              })}
            </Header>
          ))}
          {data === undefined ? (
            <LoadingContainer>
              <LoadingIndicator size={48} topPadding={64} />
            </LoadingContainer>
          ) : rows.length ? (
            <TableBodyScrollContainer
              onLayout={paginationOptions.pageSize === 'dynamic' ? onLayout : undefined}
              withScroll={withScroll}
            >
              <TableBody testID={tID('DataTable-tableBody')} role={withTableSemantics ? 'rowgroup' : undefined}>
                {rows.map((row, index) => {
                  const sharedRowProps = {
                    rowHeight,
                    rowIndex: index,
                    striped,
                    rowBorder,
                    hasStickyColumn,
                  }
                  const isHovered = hoveredRowId === row.id
                  const rowActionsContent = rowActions ? rowActions(row, isHovered) : null
                  const rowWrapperOwnsSemantics = withTableSemantics && rowActions != null

                  const renderRowContent = (rowElement: React.ReactNode) => {
                    if (rowActions) {
                      return (
                        <RowWrapper
                          key={row.id}
                          role={rowWrapperOwnsSemantics ? 'row' : undefined}
                          onMouseEnter={onRowHover ? () => onRowHover(row.id) : undefined}
                          onMouseLeave={onRowHover ? () => onRowHover(null) : undefined}
                        >
                          {rowElement}
                          {rowActionsContent && (
                            <RowActionsOverlay
                              testID={tID(`DataTable-rowActions-${row.id}`)}
                              role={rowWrapperOwnsSemantics ? 'cell' : undefined}
                            >
                              {rowActionsContent}
                            </RowActionsOverlay>
                          )}
                        </RowWrapper>
                      )
                    }
                    return rowElement
                  }

                  if (onRowClick != null) {
                    const clickableRowProps = {
                      testID: tID('DataTable-dataRow'),
                      role: withTableSemantics && !rowWrapperOwnsSemantics ? ('row' as const) : undefined,
                      isClickable: true,
                      isHovered,
                      ...sharedRowProps,
                    }

                    return renderRowContent(
                      withTableSemantics ? (
                        <RowView
                          key={row.id}
                          onClick={() => onRowClick(row)}
                          onMouseEnter={onRowHover && !rowActions ? () => onRowHover(row.id) : undefined}
                          onMouseLeave={onRowHover && !rowActions ? () => onRowHover(null) : undefined}
                          {...clickableRowProps}
                        >
                          {renderCells(row, index, isHovered)}
                        </RowView>
                      ) : (
                        <ClickableRow
                          key={row.id}
                          onPress={() => onRowClick(row)}
                          hoveredBackgroundColor={colorsV1.charcoal1}
                          pressedBackgroundColor={colorsV1.charcoal1}
                          onHoverIn={onRowHover && !rowActions ? () => onRowHover(row.id) : undefined}
                          onHoverOut={onRowHover && !rowActions ? () => onRowHover(null) : undefined}
                          {...clickableRowProps}
                        >
                          {renderCells(row, index, isHovered)}
                        </ClickableRow>
                      ),
                    )
                  }
                  if (onRowHover != null) {
                    return renderRowContent(
                      <RowView
                        key={row.id}
                        testID={tID('DataTable-dataRow')}
                        role={withTableSemantics && !rowWrapperOwnsSemantics ? 'row' : undefined}
                        onMouseEnter={!rowActions ? () => onRowHover(row.id) : undefined}
                        onMouseLeave={!rowActions ? () => onRowHover(null) : undefined}
                        {...sharedRowProps}
                      >
                        {renderCells(row, index, isHovered)}
                      </RowView>,
                    )
                  }
                  return renderRowContent(
                    <RowView
                      key={row.id}
                      testID={tID('DataTable-dataRow')}
                      role={withTableSemantics && !rowWrapperOwnsSemantics ? 'row' : undefined}
                      {...sharedRowProps}
                    >
                      {renderCells(row, index, false)}
                    </RowView>,
                  )
                })}
              </TableBody>
            </TableBodyScrollContainer>
          ) : emptyTableComponent ? (
            emptyTableComponent
          ) : (
            <EmptyTable testID={tID('DataTable-emptyTable')}>{emptyTableText}</EmptyTable>
          )}
        </Table>
        {pagination && rows.length > 0 && (
          <DataTablePagination
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPageCount={table.getPageCount()}
            onNextPage={() => table.nextPage()}
            onPrevPage={() => table.previousPage()}
            onFirstPage={() => table.setPageIndex(0)}
            onLastPage={() => table.setPageIndex(table.getPageCount() - 1)}
            onPageChange={(page) => table.setPageIndex(page - 1)}
            customStyle={pagnationStyle}
          />
        )}
      </TableContainer>
    </OuterContainer>
  )
}
